import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";

import { findMe } from "@app/store/iam/actions";
import chatSettings from "@app/util/chat/chatSettings";
import loggingService from "@app/util/logging";

import { setChatGeometry, setChatOpen, setChatPreferences, toggleChatMaximized } from "./actions";
import { chatConversationSagas } from "./conversation/sagas";
import { getChatGeometry, getChatPreferences, isChatOpen } from "./selectors";
import { registerViewContext } from "./viewContext/actions";
import { isPageEntry } from "./viewContext/composeViewContext";
import { getViewContextEntries } from "./viewContext/selectors";
import { chatVoiceSagas } from "./voice/sagas";

// Two watchers rather than one, so a write only ever carries the slice its own trigger changed.
// Geometry and open state are per-tab and change constantly; preferences are one global setting a
// rep expects to hold everywhere. Sending all three on every drag meant a tab still holding the
// preferences it read at page load wrote that stale copy over whatever another tab had just saved.
export function* onPersistChatState() {
  yield all([
    takeEvery([setChatGeometry, setChatOpen, toggleChatMaximized], function* () {
      const geometry: ReturnType<typeof getChatGeometry> = yield select(getChatGeometry);
      const isOpen: ReturnType<typeof isChatOpen> = yield select(isChatOpen);
      yield call(chatSettings.setState, { geometry, isOpen });
    }),
    takeEvery(setChatPreferences, function* () {
      const preferences: ReturnType<typeof getChatPreferences> = yield select(getChatPreferences);
      yield call(chatSettings.setState, { preferences });
    }),
  ]);
}

// Refreshes the app-wide fix (iam position) as the rep starts chatting, so a "near me" ask
// carries a current location rather than the one captured at app boot. Fire-and-forget: a
// failed refresh keeps the previous fix (findMe.failure is not reduced into state).
export function* onRefreshUserLocation() {
  yield takeEvery(setChatOpen, function* ({ payload: isOpen }: ReturnType<typeof setChatOpen>) {
    if (isOpen) {
      yield put(findMe.request());
    }
  });
}

// Logged once per registration, not in composeViewContext, so composition stays pure.
export function* onWarnMultiplePageContexts() {
  yield takeEvery(registerViewContext, function* () {
    const entries: ReturnType<typeof getViewContextEntries> = yield select(getViewContextEntries);
    const pages = entries.filter(isPageEntry);
    if (pages.length > 1) {
      yield call(
        [loggingService, loggingService.debug],
        "More than one page registered chat view context",
        {
          anchorKeys: pages.map(({ anchorKey }) => anchorKey),
        }
      );
    }
  });
}

export function* chatSaga() {
  yield all([
    call(onPersistChatState),
    call(onRefreshUserLocation),
    call(onWarnMultiplePageContexts),
    fork(chatConversationSagas),
    fork(chatVoiceSagas),
  ]);
}
